import { useMemo, useState, useCallback } from 'react';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { formatDistance } from 'date-fns';

import { TabList, TabPanel, Tab, useMount } from '@simplywallst/ui-core';

import type { TabOnClickEvent } from '@simplywallst/ui-core';

import { cn } from '@simplywallst/ui-core/next';

import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart';
import BrazeContentCard from '@/components/BrazeContentCard';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

import { useIsMobileScreen } from '@/hooks/useMedia';

import { PortfolioSettings } from '../PortfolioSettings';
import { PortfolioPageHeader } from '../../../../components/PortfolioPageHeader';
import { HoldingsPanel } from '../../../../components/HoldingsPanel';
import { AddTransactionButton } from '../../../../components/AddTransaction';

import { TrackLandEvent } from './TrackLandEvent';
import { Footer } from '@/components/Footer';
import { useDispatch } from 'react-redux';
import { redirect } from 'redux-first-router';

import { usePortfolioType } from '@/features/portfolio/components/HoldingsTable/hooks';

import { RestrictedTab } from './RestrictedTab';
import { RestrictedTabPanel } from './RestrictedTabPanel';
import { NonTransactionalReturnsPanel } from '@/features/portfolio/components/ReturnsPanel';
import { useFlag } from '@simplywallst/unleash';

import { getRouteType } from '../../utils/portfolioTabs';
import type { PortfolioTab } from '../../utils/portfolioTabs';

const RestrictionPlanModal = loadable(
  () => import('../../../../components/RestrictionPlanModal'),
  {
    resolveComponent: (c) => c.RestrictionPlanModal,
  }
);

const InvalidHoldingsBanner = loadable(
  () => import('../InvalidHoldingsBanner'),
  {
    resolveComponent: (c) => c.InvalidHoldingsBanner,
  }
);

const AnalysisPanel = loadable(
  () => import('../../../../components/AnalysisPanel'),
  {
    resolveComponent: (c) => c.AnalysisPanel,
  }
);

const DividendPanel = loadable(
  () => import('../../../../components/DividendPanel'),
  {
    resolveComponent: (c) => c.DividendPanel,
  }
);

const NarrativePanel = loadable(
  () => import('../../../../components/NarrativePanel'),
  {
    resolveComponent: (c) => c.NarrativePanel,
  }
);

const ReturnsPanel = loadable(
  () => import('../../../../components/ReturnsPanel'),
  {
    resolveComponent: (c) => c.ReturnsPanel,
  }
);

const UpdatesPanel = loadable(
  () => import('../../../../components/UpdatesPanel'),
  {
    resolveComponent: (c) => c.UpdatesPanel,
  }
);

const RelinkModal = loadable(
  () => import('@/features/portfolio/components/RelinkModal'),
  {
    resolveComponent: (c) => c.RelinkModal,
  }
);

type Props = {
  portfolio: Portfolio;
  initialPanel: PortfolioTab;
};

export const PopulatedState = ({ portfolio, initialPanel }: Props) => {
  const updatesFlag = useFlag('mono-portfolio-updates-tab');
  const hasAccessToUpdatesPanel = updatesFlag.ready && updatesFlag.enabled;

  const [openRelinkModal, setOpenRelinkModal] = useState(false);

  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });
  const dispatch = useDispatch();

  const [selectedPanel, setSelectedPanel] = useState<PortfolioTab>(
    initialPanel ?? 'holdings'
  );

  const { Track } = useTracking({
    type: 'track',
    modifier: 'populatedState',
  });

  useMount(() => {
    window.scrollTo(0, 0);
  });

  const timeSinceLastUpdate = useMemo(() => {
    return formatDistance(new Date(portfolio.updatedAt), new Date(), {
      addSuffix: true,
    });
  }, [portfolio.updatedAt]);

  const type = usePortfolioType(portfolio);

  const isManualTransactionsPortfolio = type === 'transaction-portfolio';

  const showInvalidHoldingsBanner =
    type === 'transaction-portfolio' && portfolio.invalidHoldings.length > 0;

  const handleTabClick = useCallback(
    (event: TabOnClickEvent<PortfolioTab>) => {
      const tab = event.target.value;
      const type = getRouteType(tab);
      if (!tab || !type) return;
      setSelectedPanel(tab);
      dispatch(
        redirect({
          type,
          payload: { portfolioId: portfolio.id },
        })
      );
    },
    [dispatch, portfolio.id]
  );

  return (
    <Track>
      <MiniPriceChartDefinitions />
      <TrackLandEvent portfolio={portfolio} />
      <div className="grid w-full">
        <div className="mt-x2 w-full max-w-screen-xl justify-self-center md:my-x2 lg:px-x3">
          {showInvalidHoldingsBanner && (
            <InvalidHoldingsBanner
              portfolioId={portfolio.id}
              invalidHoldings={portfolio.invalidHoldings}
            />
          )}

          <div className="[&_.braze-content-card]:-mb-x1 [&_.braze-content-card]:mt-x1">
            <BrazeContentCard
              feedType="new-portfolio.top"
              anchorTarget="_blank"
              className="px-x2 lg:px-0"
            />
          </div>

          <PortfolioPageHeader portfolio={portfolio}>
            {isMobileScreen ? (
              <div
                className={cn(
                  'grid gap-x1',
                  isManualTransactionsPortfolio
                    ? 'grid-cols-[max-content_max-content]'
                    : 'grid-cols-[max-content]'
                )}
              >
                <PortfolioSettings portfolio={portfolio} />
                {isManualTransactionsPortfolio && (
                  <AddTransactionButton
                    variant="primary"
                    size="medium"
                    portfolioId={portfolio.id}
                  >
                    <PlusSolid /> Transactions
                  </AddTransactionButton>
                )}
              </div>
            ) : null}
            <div
              className={cn(
                'grid grid-cols-[minmax(min-content,max-content)_auto] grid-rows-[24px] items-center gap-x-5',
                portfolio.type === 'linked'
                  ? 'md:grid-cols-[auto_max-content_max-content]'
                  : 'md:grid-cols-[auto_max-content]'
              )}
            >
              <p className="m-x0 text-tiny text-subtle">
                Updated
                <span className="ml-x0_5 text-softer">
                  {timeSinceLastUpdate}
                </span>
              </p>
              {portfolio.type === 'linked' && (
                <div className="grid grid-cols-[min-content_auto] items-center gap-x1">
                  <div
                    className={cn(
                      'size-x0_75 rounded-full',
                      portfolio.link.status === 'disconnected'
                        ? 'bg-bad'
                        : 'bg-good'
                    )}
                  />
                  {portfolio.link.status === 'disconnected' ? (
                    <div className="flex items-center gap-x0_5">
                      <p className="m-x0 text-tiny text-subtle">
                        Disconnected.
                      </p>
                      <button
                        className="m-x0 p-x0 text-tiny text-solid underline"
                        onClick={() => setOpenRelinkModal(true)}
                      >
                        Relink
                        <span className="hidden md:inline">
                          {' '}
                          with {portfolio.link.brokerageName}
                        </span>
                      </button>
                    </div>
                  ) : (
                    <p className="m-x0 text-tiny text-subtle">
                      Linked with {portfolio.link.brokerageName}
                    </p>
                  )}
                </div>
              )}
              {!isMobileScreen ? (
                <div
                  className={cn(
                    'grid gap-x1',
                    isManualTransactionsPortfolio
                      ? 'grid-cols-[max-content_max-content]'
                      : 'grid-cols-[max-content]'
                  )}
                >
                  <PortfolioSettings portfolio={portfolio} />
                  {isManualTransactionsPortfolio && (
                    <AddTransactionButton
                      variant="primary"
                      size="medium"
                      portfolioId={portfolio.id}
                    >
                      <PlusSolid /> Add transaction
                    </AddTransactionButton>
                  )}
                </div>
              ) : null}
            </div>
          </PortfolioPageHeader>
          <TabList
            templateColumns="repeat(6, max-content)"
            className="w-screen overflow-x-auto scrollbar-hidden lg:w-full"
          >
            <Tab<PortfolioTab>
              value="holdings"
              selected={selectedPanel === 'holdings'}
              onClick={handleTabClick}
              styleType="subnav"
            >
              Holdings
            </Tab>
            <Tab<PortfolioTab>
              value="returns"
              selected={selectedPanel === 'returns'}
              disabled={portfolio.isAnalysisRestricted}
              onClick={handleTabClick}
              styleType="subnav"
            >
              {portfolio.isAnalysisRestricted ? (
                <RestrictedTab title="Returns" />
              ) : (
                'Returns'
              )}
            </Tab>
            <Tab<PortfolioTab>
              value="narratives"
              selected={selectedPanel === 'narratives'}
              onClick={handleTabClick}
              styleType="subnav"
            >
              Narratives
            </Tab>
            {hasAccessToUpdatesPanel && (
              <Tab<PortfolioTab>
                value="updates"
                selected={selectedPanel === 'updates'}
                onClick={handleTabClick}
                styleType="subnav"
              >
                Updates
              </Tab>
            )}
            <Tab<PortfolioTab>
              value="dividend"
              selected={selectedPanel === 'dividend'}
              disabled={portfolio.isAnalysisRestricted}
              onClick={handleTabClick}
              styleType="subnav"
            >
              {portfolio.isAnalysisRestricted ? (
                <RestrictedTab title="Dividends" />
              ) : (
                'Dividends'
              )}
            </Tab>
            <Tab<PortfolioTab>
              value="analysis"
              selected={selectedPanel === 'analysis'}
              disabled={portfolio.isAnalysisRestricted}
              onClick={handleTabClick}
              styleType="subnav"
            >
              {portfolio.isAnalysisRestricted ? (
                <RestrictedTab title="Analysis" />
              ) : (
                'Analysis'
              )}
            </Tab>
          </TabList>
          <hr className="m-x0" />
        </div>
        <TabPanel<PortfolioTab>
          value="holdings"
          selected={selectedPanel === 'holdings'}
          className="w-full max-w-screen-xl justify-self-center lg:px-x3"
        >
          <HoldingsPanel portfolio={portfolio} />
        </TabPanel>
        <TabPanel<PortfolioTab>
          value="narratives"
          selected={selectedPanel === 'narratives'}
          className="w-full max-w-screen-xl justify-self-center lg:px-x3"
        >
          <NarrativePanel portfolio={portfolio} />
        </TabPanel>
        <TabPanel<PortfolioTab>
          value="analysis"
          selected={selectedPanel === 'analysis'}
          className="w-full max-w-screen-xl justify-self-center lg:px-x3"
        >
          <RestrictedTabPanel portfolio={portfolio}>
            <AnalysisPanel portfolio={portfolio} />
          </RestrictedTabPanel>
        </TabPanel>
        <TabPanel<PortfolioTab>
          value="dividend"
          selected={selectedPanel === 'dividend'}
          className="w-full max-w-screen-xl justify-self-center lg:px-x3"
        >
          <RestrictedTabPanel portfolio={portfolio}>
            <DividendPanel portfolio={portfolio} />
          </RestrictedTabPanel>
        </TabPanel>
        <TabPanel<PortfolioTab>
          value="returns"
          selected={selectedPanel === 'returns'}
          className="w-full"
        >
          <RestrictedTabPanel portfolio={portfolio}>
            {isManualTransactionsPortfolio ? (
              <ReturnsPanel portfolio={portfolio} />
            ) : (
              <NonTransactionalReturnsPanel portfolio={portfolio} />
            )}
          </RestrictedTabPanel>
        </TabPanel>
        <TabPanel<PortfolioTab>
          value="updates"
          selected={selectedPanel === 'updates'}
          className="w-full max-w-screen-xl justify-self-center lg:px-x3"
        >
          <UpdatesPanel />
        </TabPanel>
        <div className="w-full max-w-screen-xl justify-self-center lg:px-x3">
          <Footer />
        </div>
      </div>

      {portfolio.isAnalysisRestricted && (
        <RestrictionPlanModal portfolio={portfolio} limitedType="fully" />
      )}
      {openRelinkModal && (
        <RelinkModal
          portfolio={portfolio}
          onClose={() => setOpenRelinkModal(false)}
        />
      )}
    </Track>
  );
};
